/**
 * Inline command-message rendering for the chat thread.
 *
 * A user-invoked coded command (for example `workspace_init`) renders as a
 * compact command message: name + captured
 * args, then a pending affordance, the result, or a distinct error. This is
 * the render side of the persisted session-lane pair (`reidentifyCommandMessages`)
 * and of the transient command dispatch in ChatView.
 *
 * Boundary invariant (grep-verifiable): this file calls ONLY pure logic over
 * the SDK capability types and helper view models — never `sdk.tools.*`.
 */

import type React from 'react';
import {
  JSON_ENVELOPE_TOOL_NAMES,
  type CommandMessageView,
} from './capability-palette.helpers';

/** A compact `name arg1 arg2` summary of the captured arguments. */
function argsSummary(args: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(args)) {
    if (v === undefined || v === null || v === '') continue;
    const s = typeof v === 'string' ? v : JSON.stringify(v);
    parts.push(`${k}=${s}`);
  }
  return parts.join(' ');
}

function CommandResultBody({ view }: { view: CommandMessageView }): React.ReactElement {
  const preview = view.preview ?? '';
  if (!JSON_ENVELOPE_TOOL_NAMES.has(view.toolName)) {
    return (
      <pre
        data-testid="command-result-preview"
        className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-muted/50 p-2 text-xs"
      >
        {preview}
      </pre>
    );
  }
  // Envelope tool — parse JSON. A parse failure is a real contract violation:
  // surface it visibly (no silent fallback), never crash the thread.
  let parsed: Record<string, unknown> | null = null;
  try {
    parsed = JSON.parse(preview) as Record<string, unknown>;
  } catch {
    parsed = null;
  }
  if (!parsed) {
    return (
      <div
        data-testid="command-envelope-error"
        className="mt-1 rounded bg-destructive/10 p-2 text-xs text-destructive"
      >
        Malformed {view.toolName} envelope — expected JSON.
        <pre className="mt-1 whitespace-pre-wrap">{preview}</pre>
      </div>
    );
  }
  return (
    <dl data-testid="command-result-envelope" className="mt-1 grid grid-cols-[max-content_1fr] gap-x-2 text-xs">
      {Object.entries(parsed).map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="font-medium text-muted-foreground">{k}</dt>
          <dd className="break-all">{typeof v === 'string' ? v : JSON.stringify(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Inline command message rendered in the thread: name + captured args, then a
 * pending affordance, the result, or a distinct error. A
 * `persist_failed_after_execution` error renders as "ran, result not saved" —
 * never as a generic failure.
 */
export function CommandMessage({ view }: { view: CommandMessageView }): React.ReactElement {
  const summary = argsSummary(view.args);
  return (
    <div
      data-testid="command-message"
      data-tool-name={view.toolName}
      data-status={view.status}
      data-error-kind={view.errorKind ?? ''}
      className="mx-auto w-full max-w-5xl rounded-lg bg-card px-3 py-2 text-sm"
    >
      <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
        <span className="rounded bg-primary/10 px-1.5 py-0.5 font-medium text-primary">
          /{view.toolName}
        </span>
        {summary && <span className="truncate">{summary}</span>}
      </div>

      {view.status === 'pending' && (
        <div data-testid="command-pending" className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="h-2 w-2 animate-pulse rounded-full bg-muted-foreground/60" />
          Running…
        </div>
      )}

      {view.status === 'success' && <CommandResultBody view={view} />}

      {view.status === 'error' && view.errorKind === 'persist_failed_after_execution' && (
        <div
          data-testid="command-persist-failed"
          className="mt-1 rounded bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-400"
        >
          Ran, result not saved. The command executed (its side effects
          happened) but its result could not be persisted to the conversation.
          {view.error && <div className="mt-1 opacity-80">{view.error}</div>}
        </div>
      )}

      {view.status === 'error' && view.errorKind !== 'persist_failed_after_execution' && (
        <div
          data-testid="command-error"
          className="mt-1 rounded bg-destructive/10 p-2 text-xs text-destructive"
        >
          {view.error ?? 'Command failed.'}
        </div>
      )}
    </div>
  );
}
